import { Geotab } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CastedAddInData, useRawAddinData } from "./useAddinData";
import { useGeotabClient } from "@/components/GeotabClientContext";

async function removeAddIn<T>(
  geotabClient: Geotab,
  addinId: string,
  key: keyof T,
  delSearch: string,
  previous: CastedAddInData<T>[],
): Promise<boolean> {
  try {
    const entity = previous.find(item => item.details[key] === delSearch);
    if (!entity) return false;
    await geotabClient.call("Remove", {
      typeName: "AddInData",
      entity: { id: entity.id, addInId: addinId },
    });
    return true;
  } catch (error) {
    console.error("Error removing add-in:", error);
    return false;
  }
}

async function removeAddInByMatch<T>(
  geotabClient: Geotab,
  addinId: string,
  matcher: Partial<T>,
  previous: CastedAddInData<T>[],
): Promise<boolean> {
  try {
    const matcherKeys = Object.keys(matcher) as (keyof T)[];
    const entity = previous.find(item =>
      matcherKeys.every(k => item.details[k] === matcher[k]),
    );
    if (!entity) return false;
    await geotabClient.call("Remove", {
      typeName: "AddInData",
      entity: { id: entity.id, addInId: addinId },
    });
    return true;
  } catch (error) {
    console.error("Error removing add-in:", error);
    return false;
  }
}

export function useRemoveAddInByMatch<T extends Record<string, any>>(addInId: string) {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { data: previous = [] } = useRawAddinData<T>(addInId);
  const queryKey = ["addinData", addInId, activeSessionId] as const;
  return useMutation({
    mutationFn: async ({ matcher }: { matcher: Partial<T> }) => {
      return removeAddInByMatch<T>(geotabClient, addInId, matcher, previous);
    },
    onMutate: async ({ matcher }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<CastedAddInData<T>[]>(queryKey) ?? [];
      const matcherKeys = Object.keys(matcher) as (keyof T)[];

      const updated = previousData.filter(
        item => !matcherKeys.every(k => item.details[k] === matcher[k]),
      );

      queryClient.setQueryData<CastedAddInData<T>[]>(queryKey, updated);

      return { previousData };
    },
    onError: (err, _input, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<CastedAddInData<T>[]>(queryKey, context.previousData);
      }
      console.error("Error removing add-in:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useRemoveAddIn<T extends Record<string, any>>(addInId: string, key: keyof T) {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { data: previous = [] } = useRawAddinData<T>(addInId);
  const queryKey = ["addinData", addInId, activeSessionId] as const;
  return useMutation({
    mutationFn: async ({ delSearch }: { delSearch: string }) => {
      return removeAddIn<T>(geotabClient, addInId, key, delSearch, previous);
    },
    onMutate: async ({ delSearch }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<CastedAddInData<T>[]>(queryKey) ?? [];

      const updated = previousData.filter(item => item.details[key] !== delSearch);

      queryClient.setQueryData<CastedAddInData<T>[]>(queryKey, updated);

      return { previousData };
    },
    onError: (err, input, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<CastedAddInData<T>[]>(queryKey, context.previousData);
      }
      console.error("Error removing add-in:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
